import { useState, useEffect, useRef, Fragment } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  FileText, 
  Printer, 
  Download,
  Users,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Clock,
  Building2,
  Calendar,
  TrendingUp,
  Banknote,
  Wallet,
  MinusCircle,
  ChevronDown,
  ChevronRight,
  History,
  CreditCard,
  Search,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { API } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const PayrollReport = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [expandedRows, setExpandedRows] = useState({});
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [fixingDiscrepancy, setFixingDiscrepancy] = useState({});
  const [editDialog, setEditDialog] = useState({ open: false, emp: null });
  const [editForm, setEditForm] = useState({
    gross_salary: 0, advance_recovery: 0, pf_deduction: 0,
    esi_deduction: 0, absence_deduction: 0, other_deductions: 0,
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const printRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  const runDiagnostic = async () => {
    try {
      setDiagLoading(true);
      setShowDiag(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll-report/${selectedMonth}/diagnostic`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run diagnostic');
      setShowDiag(false);
    } finally {
      setDiagLoading(false);
    }
  };

  const openEditDialog = (emp) => {
    setEditForm({
      gross_salary: emp.gross_salary || 0,
      advance_recovery: emp.advance_recovery || 0,
      pf_deduction: emp.pf_deduction || 0,
      esi_deduction: emp.esi_deduction || 0,
      absence_deduction: emp.absence_deduction || 0,
      other_deductions: emp.other_deductions || 0,
    });
    setEditDialog({ open: true, emp });
  };

  const editNum = (v) => {
    const n = parseFloat(v);
    return isNaN(n) || n < 0 ? 0 : n;
  };

  const editNetPreview = () => {
    const ded = editNum(editForm.advance_recovery) + editNum(editForm.pf_deduction)
      + editNum(editForm.esi_deduction) + editNum(editForm.absence_deduction)
      + editNum(editForm.other_deductions);
    return Math.max(editNum(editForm.gross_salary) - ded, 0);
  };

  const handleSaveEdit = async () => {
    if (!editDialog.emp) return;
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        gross_salary: editNum(editForm.gross_salary),
        advance_recovery: editNum(editForm.advance_recovery),
        pf_deduction: editNum(editForm.pf_deduction),
        esi_deduction: editNum(editForm.esi_deduction),
        absence_deduction: editNum(editForm.absence_deduction),
        other_deductions: editNum(editForm.other_deductions),
      };
      const response = await axios.put(
        `${API}/salaries/${editDialog.emp.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setEditDialog({ open: false, emp: null });
      await fetchReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update salary');
    } finally {
      setSavingEdit(false);
    }
  };


  const fixDiscrepancy = async (salaryId, employeeName, discrepancy) => {
    if (!window.confirm(
      `Reduce ${employeeName}'s recorded paid amount by ${formatCurrency(discrepancy)} to match actual payment records?\n\nThis lowers the "paid" figure and increases the balance owed. Use this when the amount was over-recorded and never actually paid.`
    )) {
      return;
    }
    setFixingDiscrepancy(prev => ({ ...prev, [salaryId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payroll-report/fix-discrepancy/${salaryId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      await Promise.all([fetchReport(), runDiagnostic()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix discrepancy');
    } finally {
      setFixingDiscrepancy(prev => ({ ...prev, [salaryId]: false }));
    }
  };

  const toggleRowExpand = (empId) => {
    setExpandedRows(prev => ({
      ...prev,
      [empId]: !prev[empId]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show the effective payment date the user entered (day-closing date).
  // Falls back to the entry timestamp for direct payments that have no date.
  const formatPaymentDate = (payment) => {
    if (payment?.date) return formatDate(payment.date);
    if (payment?.payment_date) return formatDate(payment.payment_date);
    return formatDateTime(payment?.paid_at);
  };

  const handleFixOrphaned = async (salaryId, employeeName, amount, action) => {
    const actionLabel = action === 'reset' ? 'reset to ₹0' : 'recreate payment record';
    
    if (!window.confirm(`Are you sure you want to ${actionLabel} for ${employeeName}'s orphaned payment of ${formatCurrency(amount)}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/fix-orphaned/${salaryId}?action=${action}&payment_method=cash`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchReport(); // Refresh the report
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to fix orphaned payment';
      toast.error(errorMsg);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll-report/${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch payroll report');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    setShowDiag(false);
    setDiagData(null);
  }, [selectedMonth]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'UNPAID' },
      partially_paid: { bg: 'bg-blue-100 text-blue-700', icon: AlertCircle, label: 'PARTIAL' },
      paid: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'PAID' }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {style.label}
      </Badge>
    );
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payroll Report - ${selectedMonth}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px; 
            color: #333;
            font-size: 12px;
          }
          .header { 
            text-align: center; 
            margin-bottom: 20px; 
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .header h1 { font-size: 24px; margin-bottom: 5px; }
          .header h2 { font-size: 18px; color: #666; }
          .header p { font-size: 11px; color: #888; margin-top: 5px; }
          .summary-grid { 
            display: grid; 
            grid-template-columns: repeat(4, 1fr); 
            gap: 15px; 
            margin-bottom: 20px; 
          }
          .summary-card { 
            border: 1px solid #ddd; 
            padding: 12px; 
            border-radius: 4px;
            text-align: center;
          }
          .summary-card .label { font-size: 10px; color: #666; text-transform: uppercase; }
          .summary-card .value { font-size: 16px; font-weight: bold; margin-top: 5px; }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 15px;
            font-size: 11px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 8px; 
            text-align: left; 
          }
          th { 
            background: #f5f5f5; 
            font-weight: bold;
            text-transform: uppercase;
            font-size: 10px;
          }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .status-paid { color: #16a34a; font-weight: bold; }
          .status-partial { color: #2563eb; font-weight: bold; }
          .status-unpaid { color: #ca8a04; font-weight: bold; }
          .total-row { background: #f0f9ff; font-weight: bold; }
          .footer { 
            margin-top: 30px; 
            padding-top: 15px; 
            border-top: 1px solid #ddd;
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            color: #666;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleExportExcel = () => {
    if (!reportData || !reportData.has_data) {
      toast.error('No data to export');
      return;
    }

    // Prepare data for Excel
    const excelData = reportData.employees.map((emp, index) => ({
      'S.No': index + 1,
      'Employee Name': emp.employee_name,
      'Designation': emp.designation,
      'Bank Name': emp.bank_name || '-',
      'Bank Account': emp.bank_account || '-',
      'Phone': emp.phone || '-',
      'Gross Salary': emp.gross_salary,
      'Deductions': emp.deductions,
      'Net Salary': emp.net_salary,
      'Advance Adjusted': emp.advance_adjusted || 0,
      'Paid Amount': emp.paid_amount,
      'Balance': emp.balance,
      'Status': emp.status.toUpperCase()
    }));

    // Add summary row
    excelData.push({});
    excelData.push({
      'S.No': '',
      'Employee Name': 'TOTAL',
      'Designation': `${reportData.summary.total_employees} Employees`,
      'Bank Name': '',
      'Bank Account': '',
      'Phone': '',
      'Gross Salary': reportData.summary.total_gross,
      'Deductions': reportData.summary.total_deductions,
      'Net Salary': reportData.summary.total_net,
      'Advance Adjusted': reportData.summary.total_advance_adjusted,
      'Paid Amount': reportData.summary.total_paid,
      'Balance': reportData.summary.total_pending,
      'Status': ''
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payroll Report');

    // Generate Excel file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `Payroll_Report_${selectedMonth}.xlsx`);
    toast.success('Excel exported successfully');
  };

  const getMonthName = (monthStr) => {
    const date = new Date(monthStr + '-01');
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="p-6 space-y-6" data-testid="payroll-report-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Payroll Report
          </h1>
          <p className="text-gray-500 text-sm">Monthly salary summary with employee breakdown</p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>Month:</Label>
            <Input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-48"
            />
          </div>
          
          <Button variant="outline" onClick={handlePrint} disabled={!reportData?.has_data}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>

          {isAdmin && (
            <Button
              variant="outline"
              onClick={runDiagnostic}
              disabled={diagLoading}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="run-diagnostic-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              {diagLoading ? 'Checking...' : 'Run Diagnostic'}
            </Button>
          )}
          
          <Button onClick={handleExportExcel} disabled={!reportData?.has_data}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Diagnostic Panel */}
      {showDiag && (
        <Card className="border-2 border-amber-300 bg-amber-50/40" data-testid="diagnostic-panel">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <Search className="w-5 h-5" />
                Real-time Database Check — {getMonthName(selectedMonth)}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowDiag(false)} data-testid="close-diagnostic-btn">
                Close
              </Button>
            </div>
            <p className="text-sm text-gray-600">
              Read-only check of salary records vs. actual payment records. Safe to run anytime.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {diagLoading ? (
              <div className="text-center py-6 text-gray-500">Running diagnostic...</div>
            ) : diagData ? (
              <>
                {/* Summary chips */}
                <div className="flex flex-wrap gap-3">
                  <Badge className="bg-gray-100 text-gray-700">Salaries: {diagData.total_salaries}</Badge>
                  <Badge className="bg-red-100 text-red-700">Truly missing: {diagData.summary.truly_missing}</Badge>
                  <Badge className="bg-orange-100 text-orange-700">Partial: {diagData.summary.partial_orphan}</Badge>
                  <Badge className="bg-blue-100 text-blue-700">Displaced (paid other month): {diagData.summary.displaced_reconciled_count}</Badge>
                  <Badge className="bg-amber-100 text-amber-800">Discrepancy: {formatCurrency(diagData.summary.total_discrepancy)}</Badge>
                </div>

                {/* Flagged (real problems) */}
                {diagData.flagged.length > 0 ? (
                  <div>
                    <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Problem records ({diagData.flagged.length})
                    </h4>
                    <div className="overflow-x-auto border rounded bg-white">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee</TableHead>
                            <TableHead>Issue</TableHead>
                            <TableHead className="text-right">Recorded Paid</TableHead>
                            <TableHead className="text-right">In Records</TableHead>
                            <TableHead className="text-right">Discrepancy</TableHead>
                            <TableHead className="text-center">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {diagData.flagged.map((f) => (
                            <TableRow key={f.salary_id} data-testid={`diag-flagged-${f.salary_id}`}>
                              <TableCell className="font-medium">{f.employee_name}</TableCell>
                              <TableCell>
                                <Badge className={
                                  f.classification === 'truly_missing' ? 'bg-red-100 text-red-700' :
                                  f.classification === 'partial_orphan' ? 'bg-orange-100 text-orange-700' :
                                  'bg-amber-100 text-amber-800'
                                }>
                                  {f.classification.replace('_', ' ')}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">{formatCurrency(f.recorded_paid)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(f.actual_paid_records)}</TableCell>
                              <TableCell className="text-right font-semibold text-red-600">{formatCurrency(f.discrepancy)}</TableCell>
                              <TableCell className="text-center">
                                {f.discrepancy > 0 ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-300 text-red-700 hover:bg-red-50"
                                    disabled={fixingDiscrepancy[f.salary_id]}
                                    onClick={() => fixDiscrepancy(f.salary_id, f.employee_name, f.discrepancy)}
                                    data-testid={`fix-discrepancy-${f.salary_id}`}
                                  >
                                    {fixingDiscrepancy[f.salary_id] ? 'Fixing...' : `Reduce by ${formatCurrency(f.discrepancy)}`}
                                  </Button>
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded p-3">
                    <CheckCircle className="w-5 h-5" /> No problem records — all paid amounts have matching payment records.
                  </div>
                )}

                {/* Displaced reconciled (paid in another month) */}
                {diagData.displaced_reconciled?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                      <History className="w-4 h-4" /> Paid in a different month ({diagData.displaced_reconciled.length})
                    </h4>
                    <p className="text-xs text-gray-500 mb-2">
                      These are fully paid — the payment was just recorded on a date outside this month.
                    </p>
                    <div className="overflow-x-auto border rounded bg-white">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee</TableHead>
                            <TableHead className="text-right">Recorded Paid</TableHead>
                            <TableHead>Paid On (other months)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {diagData.displaced_reconciled.map((d) => (
                            <TableRow key={d.salary_id} data-testid={`diag-displaced-${d.salary_id}`}>
                              <TableCell className="font-medium">{d.employee_name}</TableCell>
                              <TableCell className="text-right">{formatCurrency(d.recorded_paid)}</TableCell>
                              <TableCell className="text-sm text-gray-600">
                                {d.payments_other_months.map((p, i) => (
                                  <span key={i} className="inline-block mr-2">
                                    {formatCurrency(p.amount)} on {p.effective_date}
                                  </span>
                                ))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </CardContent>
        </Card>
      )}


      {loading ? (
        <div className="text-center py-12">Loading report...</div>
      ) : !reportData?.has_data ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No payroll data for {getMonthName(selectedMonth)}</p>
            <p className="text-sm mt-2">Generate salaries from Salary Master to see the report.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-blue-600 font-medium">Employees</span>
                </div>
                <p className="text-xl font-bold text-blue-700">{reportData.summary.total_employees}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-purple-600 font-medium">Gross Salary</span>
                </div>
                <p className="text-xl font-bold text-purple-700">{formatCurrency(reportData.summary.total_gross)}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-indigo-50 border-indigo-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs text-indigo-600 font-medium">Net Payable</span>
                </div>
                <p className="text-xl font-bold text-indigo-700">{formatCurrency(reportData.summary.total_net)}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-green-600 font-medium">Paid</span>
                </div>
                <p className="text-xl font-bold text-green-700">{formatCurrency(reportData.summary.total_paid)}</p>
                {reportData.summary.has_orphaned_discrepancy && (
                  <div className="text-xs text-amber-600 mt-1" title="Some salary records show payments that don't have matching payment records">
                    <p>⚠️ {formatCurrency(reportData.summary.orphaned_amount)} orphaned</p>
                    <p className="text-amber-500">({reportData.summary.orphaned_employees_count} employees)</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-xs text-red-600 font-medium">Pending</span>
                </div>
                <p className="text-xl font-bold text-red-700">{formatCurrency(reportData.summary.total_pending)}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="w-4 h-4 text-gray-600" />
                  <span className="text-xs text-gray-600 font-medium">Payment Status</span>
                </div>
                <div className="flex gap-2 text-xs mt-1">
                  <span className="text-green-600">{reportData.summary.fully_paid_count} Paid</span>
                  <span className="text-blue-600">{reportData.summary.partially_paid_count} Partial</span>
                  <span className="text-yellow-600">{reportData.summary.unpaid_count} Unpaid</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Deductions Breakdown */}
          {reportData.summary.total_deductions > 0 && (
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="pt-4">
                <h3 className="font-semibold text-orange-700 mb-3 flex items-center gap-2">
                  <MinusCircle className="w-4 h-4" />
                  Total Deductions: {formatCurrency(reportData.summary.total_deductions)}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  {reportData.summary.total_advance_recovery > 0 && (
                    <div>
                      <span className="text-gray-500">Advance Recovery</span>
                      <p className="font-semibold text-orange-700">{formatCurrency(reportData.summary.total_advance_recovery)}</p>
                    </div>
                  )}
                  {reportData.summary.total_pf > 0 && (
                    <div>
                      <span className="text-gray-500">PF</span>
                      <p className="font-semibold text-orange-700">{formatCurrency(reportData.summary.total_pf)}</p>
                    </div>
                  )}
                  {reportData.summary.total_esi > 0 && (
                    <div>
                      <span className="text-gray-500">ESI</span>
                      <p className="font-semibold text-orange-700">{formatCurrency(reportData.summary.total_esi)}</p>
                    </div>
                  )}
                  {reportData.summary.total_absence > 0 && (
                    <div>
                      <span className="text-gray-500">Absence/Late</span>
                      <p className="font-semibold text-orange-700">{formatCurrency(reportData.summary.total_absence)}</p>
                    </div>
                  )}
                  {reportData.summary.total_other > 0 && (
                    <div>
                      <span className="text-gray-500">Other</span>
                      <p className="font-semibold text-orange-700">{formatCurrency(reportData.summary.total_other)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Method Breakdown */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Banknote className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cash Payments</p>
                    <p className="text-lg font-bold text-green-700">{formatCurrency(reportData.summary.cash_payments)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Bank Transfers</p>
                    <p className="text-lg font-bold text-blue-700">{formatCurrency(reportData.summary.bank_payments)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Printable Report Section */}
          <div ref={printRef}>
            <div className="header hidden print:block">
              <h1>HOPPERS FINANCE</h1>
              <h2>Payroll Report - {getMonthName(selectedMonth)}</h2>
              <p>Generated on {formatDate(reportData.generated_at)} by {reportData.generated_by}</p>
            </div>

            {/* Employee Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Employee-wise Salary Details
                  <span className="text-sm font-normal text-gray-500 ml-2">(Click row to see payment history)</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Bank Details</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">Deductions</TableHead>
                      <TableHead className="text-right">Net Salary</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      {isAdmin && <TableHead className="text-center">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.employees.map((emp, index) => (
                      <Fragment key={emp.id}>
                        <TableRow 
                          key={emp.id} 
                          data-testid={`payroll-row-${emp.id}`}
                          className={`cursor-pointer hover:bg-gray-50 ${expandedRows[emp.id] ? 'bg-blue-50' : ''}`}
                          onClick={() => toggleRowExpand(emp.id)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-1">
                              {emp.payments?.length > 0 ? (
                                expandedRows[emp.id] ? 
                                  <ChevronDown className="w-4 h-4 text-blue-500" /> : 
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                              ) : null}
                              {index + 1}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <span className="font-medium">{emp.employee_name}</span>
                              {emp.phone && (
                                <span className="text-xs text-gray-500 block">{emp.phone}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{emp.designation}</TableCell>
                          <TableCell className="text-sm">
                            {emp.bank_name || emp.bank_account ? (
                              <div>
                                <span className="text-gray-600">{emp.bank_name || ''}</span>
                                {emp.bank_account && (
                                  <span className="text-xs text-gray-400 block">{emp.bank_account}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(emp.gross_salary)}</TableCell>
                          <TableCell className="text-right">
                            {emp.deductions > 0 ? formatCurrency(emp.deductions) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(emp.net_salary)}</TableCell>
                          <TableCell className="text-right">
                            {emp.paid_amount > 0 ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <div className="flex items-center gap-1 text-green-600">
                                  {formatCurrency(emp.paid_amount)}
                                  {emp.payments?.length > 0 && (
                                    <Badge variant="outline" className="text-xs ml-1">
                                      {emp.payments.length}
                                    </Badge>
                                  )}
                                </div>
                                {emp.has_orphaned_amount && (
                                  <span className="text-xs text-amber-600" title={`Recorded: ${formatCurrency(emp.paid_amount_recorded)}, Actual payments: ${formatCurrency(emp.paid_amount)}`}>
                                    ⚠️ +{formatCurrency(emp.orphaned_amount)} orphaned
                                  </span>
                                )}
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            {emp.balance > 0 ? formatCurrency(emp.balance) : '-'}
                          </TableCell>
                          <TableCell className="text-center">{getStatusBadge(emp.status)}</TableCell>
                          {isAdmin && (
                            <TableCell className="text-center">
                              {emp.paid_amount === 0 && (emp.payments?.length || 0) === 0 ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2"
                                  onClick={(e) => { e.stopPropagation(); openEditDialog(emp); }}
                                  data-testid={`edit-salary-${emp.id}`}
                                  title="Edit / correct this salary"
                                >
                                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                              ) : (
                                <span className="text-xs text-gray-400" title="Cannot edit — payments exist">Locked</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                        
                        {/* Payment History Expanded Row */}
                        {expandedRows[emp.id] && (
                          <TableRow key={`${emp.id}-payments`} className="bg-blue-50/50">
                            <TableCell colSpan={isAdmin ? 11 : 10} className="py-3">
                              <div className="pl-8">
                                <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                                  <History className="w-4 h-4" />
                                  Payment History for {emp.employee_name}
                                </div>
                                {emp.payments?.length > 0 ? (
                                  <div className="bg-white rounded-lg border overflow-hidden">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gray-100">
                                        <tr>
                                          <th className="px-3 py-2 text-left font-medium">Date</th>
                                          <th className="px-3 py-2 text-left font-medium">Amount</th>
                                          <th className="px-3 py-2 text-left font-medium">Method</th>
                                          <th className="px-3 py-2 text-left font-medium">Source</th>
                                          <th className="px-3 py-2 text-left font-medium">Paid By</th>
                                          <th className="px-3 py-2 text-left font-medium">Remarks</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y">
                                        {emp.payments.map((payment, pIdx) => (
                                          <tr key={payment.id || pIdx} className="hover:bg-gray-50">
                                            <td className="px-3 py-2">{formatPaymentDate(payment)}</td>
                                            <td className="px-3 py-2 font-semibold text-green-600">
                                              {formatCurrency(payment.amount)}
                                            </td>
                                            <td className="px-3 py-2">
                                              <Badge variant={payment.payment_method === 'cash' ? 'secondary' : 'default'} className="text-xs">
                                                {payment.payment_method === 'cash' ? (
                                                  <><Wallet className="w-3 h-3 mr-1" /> Cash</>
                                                ) : (
                                                  <><CreditCard className="w-3 h-3 mr-1" /> Bank</>
                                                )}
                                              </Badge>
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">
                                              {payment.dayclosing_id ? (
                                                <span className="text-orange-600">Day Closing</span>
                                              ) : payment.bank_account ? (
                                                <span className="text-blue-600">{payment.bank_account}</span>
                                              ) : (
                                                <span className="text-purple-600">Direct Payment</span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">{payment.paid_by_name || '-'}</td>
                                            <td className="px-3 py-2 text-gray-500 text-xs">{payment.remarks || '-'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="text-gray-500 text-sm p-4 bg-white rounded-lg border">
                                    <AlertCircle className="w-4 h-4 inline mr-2" />
                                    No payment records found. 
                                    {emp.paid_amount > 0 && (
                                      <div className="mt-2">
                                        <span className="text-red-600 font-medium">
                                          ⚠️ ORPHANED: {formatCurrency(emp.paid_amount)} shown as paid but no payment records exist!
                                        </span>
                                        <div className="mt-3 flex gap-2">
                                          <Button 
                                            size="sm" 
                                            variant="destructive"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleFixOrphaned(emp.id, emp.employee_name, emp.paid_amount, 'reset');
                                            }}
                                          >
                                            Reset to ₹0
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant="outline"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleFixOrphaned(emp.id, emp.employee_name, emp.paid_amount, 'recreate');
                                            }}
                                          >
                                            Recreate Payment Record
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                    
                    {/* Total Row */}
                    <TableRow className="bg-gray-50 font-bold">
                      <TableCell colSpan={4} className="text-right">TOTAL ({reportData.summary.total_employees} Employees)</TableCell>
                      <TableCell className="text-right">{formatCurrency(reportData.summary.total_gross)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(reportData.summary.total_deductions)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(reportData.summary.total_net)}</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(reportData.summary.total_paid)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(reportData.summary.total_pending)}</TableCell>
                      <TableCell></TableCell>
                      {isAdmin && <TableCell></TableCell>}
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Print Footer */}
            <div className="footer hidden print:flex">
              <div>Hoppers Finance DSR</div>
              <div>Page 1 of 1</div>
              <div>Printed: {new Date().toLocaleString('en-IN')}</div>
            </div>
          </div>
        </>
      )}

      {/* Edit Salary Dialog (admin only) */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !savingEdit && setEditDialog({ open, emp: open ? editDialog.emp : null })}>
        <DialogContent className="max-w-md" data-testid="edit-salary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4 text-blue-600" />
              Edit Salary — {editDialog.emp?.employee_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              {editDialog.emp?.designation} · {getMonthName(selectedMonth)}. Net salary recalculates automatically.
            </p>
            <div>
              <Label className="text-sm">Gross Salary</Label>
              <Input
                type="number" step="0.01" min="0"
                value={editForm.gross_salary}
                onChange={(e) => setEditForm({ ...editForm, gross_salary: e.target.value })}
                data-testid="edit-gross-salary"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Advance Recovery</Label>
                <Input type="number" step="0.01" min="0" value={editForm.advance_recovery}
                  onChange={(e) => setEditForm({ ...editForm, advance_recovery: e.target.value })}
                  data-testid="edit-advance-recovery" />
              </div>
              <div>
                <Label className="text-sm">PF</Label>
                <Input type="number" step="0.01" min="0" value={editForm.pf_deduction}
                  onChange={(e) => setEditForm({ ...editForm, pf_deduction: e.target.value })}
                  data-testid="edit-pf" />
              </div>
              <div>
                <Label className="text-sm">ESI</Label>
                <Input type="number" step="0.01" min="0" value={editForm.esi_deduction}
                  onChange={(e) => setEditForm({ ...editForm, esi_deduction: e.target.value })}
                  data-testid="edit-esi" />
              </div>
              <div>
                <Label className="text-sm">Absence/Late</Label>
                <Input type="number" step="0.01" min="0" value={editForm.absence_deduction}
                  onChange={(e) => setEditForm({ ...editForm, absence_deduction: e.target.value })}
                  data-testid="edit-absence" />
              </div>
              <div className="col-span-2">
                <Label className="text-sm">Other Deductions</Label>
                <Input type="number" step="0.01" min="0" value={editForm.other_deductions}
                  onChange={(e) => setEditForm({ ...editForm, other_deductions: e.target.value })}
                  data-testid="edit-other" />
              </div>
            </div>
            <div className="flex justify-between items-center bg-gray-50 rounded p-3">
              <span className="text-sm font-medium text-gray-600">Net Salary (auto)</span>
              <span className="text-lg font-bold text-indigo-700" data-testid="edit-net-preview">
                {formatCurrency(editNetPreview())}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, emp: null })} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit} data-testid="save-edit-salary-btn">
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollReport;
